/**
 * CR legacy-grouping notice: the exact hydration state machine and the
 * neutral effect-comparison predicate (PC-B rev 9, section 15 decision 6).
 *
 * PROBE-CLEAN LEAF: relative imports only, both of which are zero-runtime-
 * dep leaves (`./who-match`, `./keepsake/canonical-json`), so
 * scripts/probe-cr-notice.mjs loads this under tsx with default
 * resolution. No React, no Zustand, no IDB.
 *
 * The notice is an OBSERVABLE-EFFECT comparison, not a validity check for
 * new events and not a raw-key-exists predicate:
 *   - Unless corpus hydration is `ready`, DEFER (render nothing, persist
 *     nothing, reevaluate after a complete successful generation).
 *   - Derive the legacy-effective map from a COPY of the raw quarantined
 *     string, safely, into a null-prototype object.
 *   - Compute the exact store-free VisibleGroupingProjectionV1 under that
 *     map and under the null-prototype empty map with the SHARED explicit-
 *     map helpers.
 *   - SHOW exactly when the two RFC 8785 canonical serializations differ;
 *     otherwise HIDE.
 * Dismissed-pair bytes never participate. The raw strings and
 * localStorage remain byte-for-byte unchanged; neither derived map is
 * installed. Owner-neutral: both inputs are device state of unknown
 * provenance, so the copy never attributes an account.
 */

import type { Prayer } from '../types/prayer';
import {
  emptyIdentityMap,
  peopleInPrayerWithMap,
  whoKey,
  whoOfWithMap,
  type WhoMergeMap,
} from './who-match';
import { jcsCanonicalize } from './keepsake/canonical-json';

/**
 * PrayerCorpusHydrationV1 (PC-B rev 9, decision 6). `generation` advances
 * only when one COMPLETE load installs. Initial false-not-loading and
 * load failure can never masquerade as a ready empty corpus.
 */
export interface PrayerCorpusHydrationV1 {
  state: 'not-started' | 'loading' | 'ready' | 'failed';
  generation: number;
}

export type LegacyGroupingNoticeResult = 'defer' | 'hide' | 'show';

export interface VisibleGroupingProjectionV1 {
  schema: 'adia-visible-grouping-projection-v1';
  prayers: Array<{
    id: string;
    primaryDisplay: string;
    primaryThreadKey: string;
    people: Array<{ key: string; display: string }>;
  }>;
}

/**
 * Derive the legacy-effective map from a COPY of the raw quarantined
 * string using the intended current filter, SAFELY: JSON parse; require a
 * non-null, non-array object; enumerate OWN entries; retain only entries
 * whose key and value are non-empty strings into a null-prototype object.
 * Invalid JSON, invalid top-level shape, or no retained entry yields a
 * null-prototype EMPTY map. A partially malformed object may retain its
 * valid own entries, without setter or prototype effects (assignment onto
 * a null-prototype object makes `__proto__` an ordinary own key).
 */
export function deriveLegacyEffectiveMap(raw: string | null): WhoMergeMap {
  const out = emptyIdentityMap();
  if (typeof raw !== 'string' || raw === '') return out;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return out;
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return out;
  }
  for (const k of Object.keys(parsed as Record<string, unknown>)) {
    const v = (parsed as Record<string, unknown>)[k];
    if (typeof k === 'string' && k !== '' && typeof v === 'string' && v !== '') {
      out[k] = v;
    }
  }
  return out;
}

/**
 * The exact store-free visible-grouping projection (decision 6 step 3).
 * Assignment is exact: `primaryDisplay` and `primaryThreadKey` are BOTH
 * the primary display string (Remember buckets on the canonical display,
 * not whoKey). `peopleInPrayerWithMap` performs its first-seen own-key
 * deduplication; only then is each returned display mapped to
 * `{key: whoKey(display), display}`. Prayers sort by id; the
 * already-deduplicated people sort by key then display. No second
 * competing dedupe, no pre-sorting of raw mentions.
 */
export function visibleGroupingProjection(
  prayers: readonly Prayer[],
  map: WhoMergeMap,
): VisibleGroupingProjectionV1 {
  const rows = prayers.map((prayer) => {
    const primary = whoOfWithMap(prayer, map);
    const people = peopleInPrayerWithMap(prayer, map).map((display) => ({
      key: whoKey(display),
      display,
    }));
    people.sort((a, b) => {
      if (a.key !== b.key) return a.key < b.key ? -1 : 1;
      if (a.display !== b.display) return a.display < b.display ? -1 : 1;
      return 0;
    });
    return {
      id: prayer.id,
      primaryDisplay: primary,
      primaryThreadKey: primary,
      people,
    };
  });
  rows.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  return { schema: 'adia-visible-grouping-projection-v1', prayers: rows };
}

/**
 * `legacyGroupingNotice(rawMerge, hydrationState, prayers)` returns
 * `defer`, `hide`, or `show` (decision 6). Only a completed hydration
 * generation decides; every other state defers. Dismissed-pair bytes are
 * deliberately NOT a parameter.
 */
export function legacyGroupingNotice(
  rawMerge: string | null,
  hydrationState: PrayerCorpusHydrationV1,
  prayers: readonly Prayer[],
): LegacyGroupingNoticeResult {
  if (hydrationState.state !== 'ready') return 'defer';
  const legacyEffectiveMap = deriveLegacyEffectiveMap(rawMerge);
  const underLegacy = jcsCanonicalize(
    visibleGroupingProjection(prayers, legacyEffectiveMap),
  );
  const underEmpty = jcsCanonicalize(
    visibleGroupingProjection(prayers, emptyIdentityMap()),
  );
  return underLegacy === underEmpty ? 'hide' : 'show';
}
